import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
// import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import { Label } from "@mui/icons-material";
import useTailwindBrand from "../Theme/useTailwindBrand";
import { DV2 } from "../App/DeclarationV2/theme";
import { Button as Dv2Button } from "../App/DeclarationV2/layout/Ui";
import ButtonField from "./Ui/ButtonField";
import { useSettingsStyle } from "./Settings";


export interface SettingsSectionDisplayProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

export interface SettingsSectionActionsProps {
  isEditing?: boolean;
  loading?: boolean;
  onEditClick?: () => void;
  children?: React.ReactNode;
  onSave?: () => void;
  onCancel?: () => void;
  className?: string;
}

export type SettingsSectionProps = SettingsSectionDisplayProps &
  SettingsSectionActionsProps;

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  subtitle,
  icon,
  isEditing = false,
  onEditClick,
  children,
  onSave,
  onCancel,
  loading,
  className
}) => {
  const { brandConfig } = useTailwindBrand()
  const variant = useSettingsStyle();

  // Maquettes 8a à 8e : une carte par groupe de champs, « Modifier » à droite, Annuler / Enregistrer en pied.
  if (variant === "v2") {
    const ff = brandConfig.fontFamily;
    return (
      <Grid item xs={12}>
        <section style={{ background: DV2.surface, border: `1px solid ${DV2.border}`, borderRadius: DV2.radius, padding: 22, fontFamily: ff, color: brandConfig.colors.text }} className={className}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {title && <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>{title}</h2>}
              {subtitle && <div style={{ fontSize: 13, color: DV2.muted, marginTop: 2 }}>{subtitle}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {icon}
              {!isEditing && onEditClick && (
                <button
                  type="button"
                  onClick={onEditClick}
                  style={{ fontFamily: ff, fontSize: 13, fontWeight: 800, color: brandConfig.colors.primary, background: "none", border: `1px solid ${DV2.border}`, borderRadius: 10, padding: "7px 14px", cursor: "pointer" }}
                >
                  Modifier
                </button>
              )}
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Grid container spacing={2}>
              {children}
            </Grid>
          </div>
          {isEditing && (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2" style={{ marginTop: 20 }}>
              <Dv2Button label="Annuler" variant="secondary" size="sm" onClick={onCancel} disabled={loading} />
              <Dv2Button label="Enregistrer" size="sm" onClick={onSave} loading={loading} />
            </div>
          )}
        </section>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sx={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }} >
      <Card variant="outlined" sx={{ mb: 3, overflow: "hidden"}} >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            p: 3
          }}
          className={className}
        >
          {/* <PersonIcon sx={{ mr: 1, color: brandConfig.colors.primary }} /> */}
          <Box>
            <Typography variant="h5" component="h2" sx={{ color: brandConfig.colors.primary, fontFamily: brandConfig.fontFamily }} className="font-bold">
              {title}
            </Typography>
            <Typography component="h5" sx={{ color: brandConfig.colors.text, fontFamily: brandConfig.fontFamily }} className="text-sm">
              {subtitle}
            </Typography>
          </Box>
          {icon}
          {!isEditing && onEditClick && (
            <IconButton
              onClick={onEditClick}
              sx={{ ml: "auto", color: brandConfig.colors.primary, borderColor: brandConfig.colors.primary }}
              aria-label="Modifier"

            >
              <EditIcon sx={{ fontSize: 18 }} />
              <label className="md:text-base text-sm font-bold">Modifier</label>
              <Label sx={{ fontSize: 12 }} className="font-bold"/>
            </IconButton>
          )}
        </Box>
        <CardContent sx={{ p: 3, height : "100%" }}>
          <Grid container spacing={2} height={"100%"}>
            {children}
            {isEditing && (
              <div className="flex lg:flex-row flex-col mt-10 justify-between items-center gap-4 w-full">
                <ButtonField
                  label="Enregistrer"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={onSave}
                  loading={loading}
                  fullWidth
                />
                <ButtonField
                  label="Annuler"
                  variant="outlined"
                  startIcon={<CancelTwoToneIcon />}
                  onClick={onCancel}
                  fullWidth
                />
              </div>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default SettingsSection;
